import React from "react";
import {DraggableCore, DraggableEventHandler} from "react-draggable";
import {Rect} from "../../models/Rect";

type ResizableBoxProps = {
    elementRef: React.RefObject<HTMLDivElement>;
    cover?: boolean;
    grabSize?: number;
    zIndex?: number;
    onResize?: (rect: Rect, deltaRect: Rect) => void;
    minWidth?: number;
    minHeight?: number;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const ResizableBox = (props: ResizableBoxProps) => {

    const grab_size = props.grabSize || 10;
    const z_index = props.zIndex || 50;
    const border_adjust_size = 2;
    const border_color = '#F0F'; // #0068b7

    const [initial_rect, parent_position_is_relative] = React.useMemo(() => {
        if (props.elementRef.current) {
            const e = props.elementRef.current;
            if (getComputedStyle(e).position === 'relative') {
                return [{
                    top: 0,
                    left: 0,
                    height: e.offsetHeight,
                    width: e.offsetWidth,
                }, true]
            } else {
                return [{
                    top: e.offsetTop,
                    left: e.offsetLeft,
                    height: e.offsetHeight,
                    width: e.offsetWidth,
                }, false]
            }
        }

        return [{top: 10, left: 10, width: 10, height: 10}, false];
    }, [props.elementRef]);

    // セルのサイズが変更された際に追随するための仕掛け
    React.useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target instanceof HTMLDivElement) {
                    //console.log("resize", entry.contentRect, entry.contentBoxSize, entry.target.offsetWidth);
                    if (!parent_position_is_relative) {
                        setTop(entry.target.offsetTop);
                        setLeft(entry.target.offsetLeft);
                    }
                    setHeight(entry.target.offsetHeight);
                    setWidth(entry.target.offsetWidth);
                }
            }
        });

        if (props.elementRef.current) {
            resizeObserver.observe(props.elementRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        }
    }, [props.elementRef]);

    const [top, setTop] = React.useState(initial_rect.top);
    const [left, setLeft] = React.useState(initial_rect.left);
    const [height, setHeight] = React.useState(initial_rect.height);
    const [width, setWidth] = React.useState(initial_rect.width);
    const [startRect, setStartRect] = React.useState(initial_rect);
    const [isDrag, setIsDrag] = React.useState(false);
    const has_cover = isDrag || props.cover !== false;

    const baseStyle: React.CSSProperties = {
        position: 'absolute',
        backgroundColor: 'transparent',
        zIndex: z_index,
    }

    const popStyle: React.CSSProperties = {
        display: isDrag ? 'block' : 'none',
        position: 'absolute',
        padding: 5,
        color: '#FFFFFF',
        backgroundColor: 'rgb(0, 104, 183, 0.8)',
        borderRadius: '5px',
        zIndex: z_index,
        top: top + height + 10,
        left: 0,
        transform: `translateX(${left + (width / 2) - 50}px)`  /* left で指定すると、ウィンドウ右端を超える位置の場合にテキスト幅が適切に確保されない */
    }

    const pop_triangle_style: React.CSSProperties = {
        display: 'block',
        position: 'absolute',
        top: -10,
        left: 'calc(50% - 5px)',
        border: '5px solid transparent',
        borderBottomColor: 'rgb(0, 104, 183, 0.8)'
    }

    const coverStyle: React.CSSProperties = {
        ...baseStyle,
        ...{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: border_color,
            width: width - border_adjust_size,
            height: height - border_adjust_size,
            top: top,
            left: left
        }
    }

    const sideRightStyle: React.CSSProperties = {
        ...baseStyle,
        ...{
            cursor: 'e-resize',
            //backgroundColor: 'rgba(0,145, 255, 0.2)',
            width: grab_size,
            height: height - grab_size,
            top: top,
            left: left + width - grab_size / 2 - border_adjust_size,
        }
    }
    const sideBottomStyle: React.CSSProperties = {
        ...baseStyle,
        ...{
            cursor: 's-resize',
            //backgroundColor: 'rgba(0,145, 255, 0.2)',
            width: width - grab_size,
            height: grab_size,
            top: top + height - grab_size / 2 - border_adjust_size,
            left: left,
        }
    }
    const vertexBottomRightStyle: React.CSSProperties = {
        ...baseStyle,
        ...{
            cursor: 'se-resize',
            width: 0,
            height: 0,
            top: top + height - grab_size - 5 - border_adjust_size,
            left: left + width - grab_size - 5 - border_adjust_size,
            borderWidth: grab_size * 2 - 5,
            borderStyle: 'solid',
            borderColor: 'transparent',
            borderTopColor: border_color,
            transform: 'rotate(-45deg)',
        }
    }

    const resize = (delta_top: number, delta_left: number, delta_height: number, delta_width: number) => {
        if (delta_top) {
            setTop(top + delta_top);
        }
        if (delta_left) {
            setLeft(left + delta_left);
        }
        if (delta_height) {
            if (props.minHeight !== undefined && (height + delta_height) < props.minHeight) {
                setHeight(props.minHeight);
            } else {
                setHeight(height + delta_height);
            }
        }
        if (delta_width) {
            if (props.minWidth !== undefined && (width + delta_width) < props.minWidth) {
                setWidth(props.minWidth);
            } else {
                setWidth(width + delta_width);
            }
        }
    }

    const handleDragStart = () => {
        setStartRect({top: top, left: left, height: height, width: width});
        setIsDrag(true);
    }
    const handleDragStop = () => {
        if (props.onResize) {
            const delta = {
                top: top - startRect.top,
                left: left - startRect.left,
                height: height - startRect.height,
                width: width - startRect.width
            };

            if (delta.top !== 0 || delta.left !== 0 || delta.height !== 0 || delta.width !== 0) {
                props.onResize({
                    top: top,
                    left: left,
                    height: height,
                    width: width
                }, delta);
            }
        }
        setIsDrag(false);
    }

    const handleRightResize: DraggableEventHandler = (e, data) => {
        e.stopPropagation();
        resize(0, 0, 0, data.deltaX);
    }
    const handleBottomResize: DraggableEventHandler = (e, data) => {
        e.stopPropagation();
        resize(0, 0, data.deltaY, 0);
    }

    const handleBottomRightResize: DraggableEventHandler = (e, data) => {
        e.stopPropagation();
        resize(0, 0, data.deltaY, data.deltaX);
    }

    return (
        <>
            {has_cover && <div style={coverStyle} onClick={(e) => props.onClick?.(e)}/>}
            <DraggableCore onStart={handleDragStart} onStop={handleDragStop} onDrag={handleBottomRightResize}>
                <div style={vertexBottomRightStyle} onClick={e => e.stopPropagation()}/>
            </DraggableCore>
            <DraggableCore onStart={handleDragStart} onStop={handleDragStop} onDrag={handleRightResize}>
                <div style={sideRightStyle} onClick={e => e.stopPropagation()}/>
            </DraggableCore>
            <DraggableCore onStart={handleDragStart} onStop={handleDragStop} onDrag={handleBottomResize}>
                <div style={sideBottomStyle} onClick={e => e.stopPropagation()}/>
            </DraggableCore>
            <div title={`${top}, ${left}, ${height}, ${width}`}
                 style={popStyle}>{width} x {height}<span style={pop_triangle_style}/></div>
        </>
    );
}
